/**
 * Helper functions for Batch Payments feature
 */

import {
  PAYMENT_METHODS,
  ENABLED_PAYMENT_METHOD_TYPES,
  BATCH_PAYMENT_INVOICE_SELECTION,
} from './constants';

import { UnpaidInvoice } from 'types/Insight';
import { COUNTRY } from 'types/constants';

/**
 * Sort invoices with default invoice first
 */
export const sortInvoicesWithDefaultFirst = (invoices: UnpaidInvoice[]): UnpaidInvoice[] => {
  return invoices.sort((a, b) => {
    if (a.defaultInvoice) return -1;
    if (b.defaultInvoice) return 1;
    return 0;
  });
};

/**
 * Get the maximum selectable invoice balance for batch payments
 * Aligns with regular payment limits based on region and feature flags
 * - Default (CC/DC): $100,000
 * - With cp-increase-max-amount-limit feature flag in US: $500,000
 */
export const getMaxSelectableInvoiceBalance = (
  region: string | undefined,
  featureFlags: Record<string, boolean> | undefined,
  amountConfig: { maxAllowedAmount: number; maxAllowedAmountCcDcACH: number } | undefined
): number => {
  // Check if merchant qualifies for increased limit through feature flag
  const hasIncreasedLimit =
    region === 'US' && featureFlags?.['cp-increase-max-amount-limit'] === true;

  if (hasIncreasedLimit && amountConfig?.maxAllowedAmountCcDcACH) {
    return amountConfig.maxAllowedAmountCcDcACH; // $500,000
  }

  return amountConfig?.maxAllowedAmount || 100000; // Default $100,000
};

/**
 * Check if an invoice is selectable based on dynamic balance limits
 * @param invoice - The invoice to check
 * @param maxBalance - The maximum allowed balance (from getMaxSelectableInvoiceBalance)
 * @returns true if selectable (balance <= maxBalance OR is the default invoice which can be edited)
 */
export const isInvoiceSelectable = (invoice: UnpaidInvoice, maxBalance: number): boolean => {
  // Default invoice is always selectable (can edit amount for partial payment)
  if (invoice.defaultInvoice) {
    return true;
  }
  // Non-default invoices are only selectable if within the limit
  return invoice.balance <= maxBalance;
};

/**
 * Calculate if edit amount should be disabled based on selected invoices
 * Edit is only enabled when ONLY the default invoice is selected
 */
export const shouldDisableEditAmount = (selectedInvoices: UnpaidInvoice[]): boolean => {
  // No invoices selected -> disable edit
  if (selectedInvoices.length === 0) return true;

  // Multiple invoices selected -> disable edit
  if (selectedInvoices.length > 1) return true;

  // Single non-default invoice selected -> disable edit
  if (selectedInvoices.length === 1 && !selectedInvoices[0].defaultInvoice) return true;

  // Only default invoice selected -> allow edit
  return false;
};

/**
 * Build payment methods array from enabled wallet payment methods
 */
export const buildPaymentMethods = (enabledPaymentMethods: string[]): string[] => {
  const paymentMethods: string[] = [];

  if (enabledPaymentMethods.includes(ENABLED_PAYMENT_METHOD_TYPES.BANK)) {
    paymentMethods.push(PAYMENT_METHODS.ACH);
  }

  if (enabledPaymentMethods.includes(ENABLED_PAYMENT_METHOD_TYPES.EFT)) {
    paymentMethods.push(PAYMENT_METHODS.EFT);
  }

  const ccTypes = [
    ENABLED_PAYMENT_METHOD_TYPES.CREDIT_CARD,
    ENABLED_PAYMENT_METHOD_TYPES.DEBIT_CARD,
    ENABLED_PAYMENT_METHOD_TYPES.DEBIT_CREDIT,
  ];

  if (enabledPaymentMethods.some((method) => ccTypes.includes(method as any))) {
    paymentMethods.push(PAYMENT_METHODS.CC);
  }

  return paymentMethods;
};

/**
 * Build current page link without source parameter
 */
export const buildCurrentPageLink = (): string => {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.href);
  url.searchParams.delete('source');
  return url.toString();
};

/**
 * Create a unique identifier string from invoice saleIds for change detection
 * Uses saleId as the primary unique identifier (always present and unique)
 */
export const createInvoiceIdsKey = (invoices: any[]): string => {
  return invoices
    .map((inv) => inv.saleId || '')
    .filter(Boolean) // Remove empty strings
    .sort()
    .join(',');
};

/**
 * Calculate total amount of selected invoices
 * @param invoices - Array of invoices
 * @param maxSelectableBalance - Optional max balance to cap individual invoice amounts
 */
export const calculateTotalAmount = (
  invoices: UnpaidInvoice[],
  maxSelectableBalance?: number
): number => {
  return invoices
    .filter((invoice) => invoice.selected)
    .reduce((total, invoice) => {
      // Cap the amount if maxSelectableBalance is provided and invoice exceeds it
      const cappedAmount =
        maxSelectableBalance && invoice.balance > maxSelectableBalance
          ? maxSelectableBalance
          : invoice.balance;
      return total + cappedAmount;
    }, 0);
};

/**
 * Get allowed payment methods for batch payments based on selected invoices
 * Maps invoice payment methods (ACH, CC) to actual payment method types
 * - ACH -> 'bank'
 * - CC -> 'dc', 'cc' (Apple Pay is not supported for batch payments)
 * Never shows PayPal/Venmo/Apple Pay for batch payments
 * @param region - Optional region ('US' | 'CA') to show only the correct bank form (ACH for US, EFT for CA) when default invoice is not selected
 */
export const getAllowedBatchPaymentMethods = (
  selectedInvoices: UnpaidInvoice[],
  enabledPaymentMethods: string[],
  region?: string
): string[] => {
  if (!selectedInvoices || selectedInvoices.length === 0) {
    return [];
  }
  // Get the intersection of payment methods across all selected invoices.
  // Normalize so ACH/EFT/Bank count as one "bank" for intersection (CA may use EFT, US ACH; same tab).
  const normalizeForIntersection = (method: string): string => {
    if (method === PAYMENT_METHODS.ACH || method === PAYMENT_METHODS.EFT || method === 'Bank') {
      return 'BANK';
    }
    if (method === PAYMENT_METHODS.CC || method === 'Card') {
      return 'CC';
    }
    return method;
  };
  const getNormalizedSet = (methods: string[] | undefined) =>
    new Set((methods || []).map(normalizeForIntersection));

  const commonNormalized = selectedInvoices.reduce<Set<string> | null>((common, invoice) => {
    const invSet = getNormalizedSet(invoice.paymentMethods);
    if (common === null) {
      return invSet;
    }
    return new Set([...common].filter((m) => invSet.has(m)));
  }, null);

  if (!commonNormalized || commonNormalized.size === 0) {
    return [];
  }

  // Check if default invoice is in the selection
  const hasDefaultInvoice = selectedInvoices.some((inv) => inv.defaultInvoice);

  const allowedMethods = new Set<string>();

  // Map normalized common methods to wallet payment method types (ACH/EFT/Bank -> bank/eft tabs)
  commonNormalized.forEach((method) => {
    if (method === PAYMENT_METHODS.BANK) {
      if (hasDefaultInvoice) {
        if (enabledPaymentMethods.includes(ENABLED_PAYMENT_METHOD_TYPES.EFT)) {
          allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.EFT);
        }
        if (enabledPaymentMethods.includes(ENABLED_PAYMENT_METHOD_TYPES.BANK)) {
          allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.BANK);
        }
      } else {
        // Default invoice not selected - show only the region-appropriate bank form (ACH for US, EFT for CA). Hide both if region unknown.
        if (region === COUNTRY.CA) {
          allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.EFT);
        } else if (region === COUNTRY.US) {
          allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.BANK);
        }
      }
    } else if (method === PAYMENT_METHODS.CC) {
      if (hasDefaultInvoice) {
        if (enabledPaymentMethods.includes(ENABLED_PAYMENT_METHOD_TYPES.DEBIT_CREDIT)) {
          allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.DEBIT_CREDIT);
        } else {
          if (enabledPaymentMethods.includes(ENABLED_PAYMENT_METHOD_TYPES.DEBIT_CARD)) {
            allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.DEBIT_CARD);
          }
          if (enabledPaymentMethods.includes(ENABLED_PAYMENT_METHOD_TYPES.CREDIT_CARD)) {
            allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.CREDIT_CARD);
          }
        }
      } else {
        allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.DEBIT_CARD);
        allowedMethods.add(ENABLED_PAYMENT_METHOD_TYPES.CREDIT_CARD);
      }
    }
  });

  // Convert Set to Array and filter out any PayPal/Venmo/Apple Pay/Affirm BNPL
  return Array.from(allowedMethods).filter(
    (method) => !['paypal', 'paypal_ppaam', 'venmo', 'pp', 'ap', 'affirm_bnpl'].includes(method)
  );
};

/**
 * Get the default invoice number for batch payments
 * Tries multiple sources in order of preference:
 * 1. From batchUnpaidInvoices array (invoice with defaultInvoice flag)
 * 2. From current sale object (the invoice being viewed)
 */
export const getDefaultInvoiceNumber = (batchUnpaidInvoices: any, sale: any): string => {
  // Try to find default invoice in the batch unpaid invoices array
  const allInvoices = batchUnpaidInvoices?.unpaidInvoices || [];
  const defaultInvoice = allInvoices.find((inv: any) => inv?.defaultInvoice);

  if (defaultInvoice) {
    return defaultInvoice.docNumber || defaultInvoice.saleId || '';
  }

  // Fallback: get from current sale (the invoice being viewed)
  return sale?.referenceNumber || sale?.id || '';
};

/**
 * Determine which invoices should be selected by default based on IXP treatment
 * @param invoices - Array of all batch invoices
 * @param treatment - IXP treatment key
 * @param categorizedInvoices - Invoices categorized by payment method
 * @param maxSelectableBalance - Maximum balance allowed for selection (optional)
 * @returns Array of invoice saleIds that should be selected
 */
export const getDefaultSelectedInvoices = (
  invoices: UnpaidInvoice[],
  invoiceSelection: BATCH_PAYMENT_INVOICE_SELECTION,
  categorizedInvoices: {
    bankOrCardInvoices: UnpaidInvoice[];
    bankOnlyInvoices: UnpaidInvoice[];
    cardOnlyInvoices: UnpaidInvoice[];
  },
  maxSelectableBalance?: number
): string[] => {
  const defaultInvoice = invoices.find((inv) => inv.defaultInvoice);
  if (!defaultInvoice) return [];

  // Check if default invoice exceeds the max amount
  const isDefaultInvoiceOverLimit =
    maxSelectableBalance !== undefined && defaultInvoice.balance > maxSelectableBalance;

  // If default invoice is over the limit, ONLY select the default invoice
  // (other checkboxes are disabled in this case)
  if (isDefaultInvoiceOverLimit) {
    return [defaultInvoice.saleId];
  }

  // Treatment: Only default invoice selected (original behavior)
  if (
    invoiceSelection === BATCH_PAYMENT_INVOICE_SELECTION.DEFAULT_INVOICE_ONLY ||
    !invoiceSelection
  ) {
    return [defaultInvoice.saleId];
  }

  // Treatment: All selectable invoices in same table as default invoice
  if (invoiceSelection === BATCH_PAYMENT_INVOICE_SELECTION.ALL_IN_SAME_TABLE) {
    const { bankOrCardInvoices, bankOnlyInvoices, cardOnlyInvoices } = categorizedInvoices;

    // Find which table the default invoice belongs to
    let tableInvoices: UnpaidInvoice[] = [];

    if (bankOrCardInvoices.some((inv) => inv.defaultInvoice)) {
      tableInvoices = bankOrCardInvoices;
    } else if (bankOnlyInvoices.some((inv) => inv.defaultInvoice)) {
      tableInvoices = bankOnlyInvoices;
    } else if (cardOnlyInvoices.some((inv) => inv.defaultInvoice)) {
      tableInvoices = cardOnlyInvoices;
    }

    // Filter to only include selectable invoices (not disabled)
    const selectableInvoices = tableInvoices.filter(
      (inv) =>
        maxSelectableBalance !== undefined ? isInvoiceSelectable(inv, maxSelectableBalance) : true // If no limit specified, all invoices are selectable
    );

    // Return all selectable invoice saleIds from the same table
    return selectableInvoices.map((inv) => inv.saleId);
  }

  // Fallback: only default invoice
  return [defaultInvoice.saleId];
};

/**
 * Get the appropriate tooltip message for disabled edit amount button
 * Returns the translation key and values for react-intl FormattedMessage
 */
export const getEditAmountTooltipConfig = (
  selectedInvoices: UnpaidInvoice[],
  defaultInvoiceNumber: string
): { id: string; defaultMessage: string; values?: Record<string, string> } | null => {
  // Case 1: No invoices selected
  if (selectedInvoices.length === 0) {
    return {
      id: 'EDIT_AMOUNT_NO_INVOICES_SELECTED',
      defaultMessage: 'Choose an invoice to edit its amount',
    };
  }

  // Case 2: Only one non-default invoice selected
  if (selectedInvoices.length === 1 && !selectedInvoices[0]?.defaultInvoice) {
    const invoiceNumber = selectedInvoices[0]?.docNumber || selectedInvoices[0]?.saleId;
    return {
      id: 'EDIT_AMOUNT_SINGLE_NON_DEFAULT',
      defaultMessage: 'To pay Invoice {invoiceNumber} partially, please view it in a new tab.',
      values: { invoiceNumber },
    };
  }

  // Case 3: Multiple invoices selected
  if (selectedInvoices.length > 1) {
    return {
      id: 'EDIT_AMOUNT_MULTIPLE_INVOICES',
      defaultMessage:
        'You can pay Invoice {invoiceNumber} partially, or multiple invoices in full.',
      values: { invoiceNumber: defaultInvoiceNumber },
    };
  }

  // Default case
  return {
    id: 'EDIT_AMOUNT_DISABLED_BATCH',
    defaultMessage: 'You can only edit the amount on single invoices.',
  };
};
